import { BehaviorSubject } from "rxjs";
let ws = null;
export let seriesComponentMapping = {};
let onCloseListener, onErrorListener;
import store from "@/store/index";
import Vue from "vue";
import downloadPerf from "@/js/download/perf";
import {
  ITEM_STATES,
  canFinalizeSeriesDownload,
  createSeriesDownloadTracker,
  setSeriesItemInFlight,
  setSeriesItemParseQueued,
  transitionSeriesItemState
} from "@/js/download/seriesState";
import {
  DOWNLOAD_TRANSPORT_MODE,
  normalizeDownloadTransportMode
} from "@/js/download/transferPolicy";
import {
  DOWNLOAD_SCHEDULING_MODE,
  normalizeDownloadSchedulingMode
} from "@/js/download/schedulingPolicy";
import { getDownloadBatchRequestMaxItems } from "@/js/download/config";
import { DownloadScheduler } from "@/js/download/scheduler";
import {
  clearSeriesRuntimeState as clearSeriesRuntimeStateStore,
  countParseQueueForSeries as countParseQueueForSeriesStore,
  countPendingChunksForSeries as countPendingChunksForSeriesStore,
  decrementInFlightDownloadsByValue,
  decrementInFlightDownloadsValue,
  handleFatalSocketPayloadError,
  resetPipelineRuntimeState,
  supportsParseBytes,
  toUint8Array,
  yieldToMainThread
} from "@/js/download/seriesDownload.runtime";
import {
  addSeriesRenderableImage,
  buildDeclaredImageIndexMap,
  buildPriorityPendingOrderForActiveAxis,
  buildPriorityPendingOrder,
  buildPriorityPendingOrderForTimeFrame,
  clampSliceIndex,
  getDeclaredSliceIndex,
  normalizeFramesPerSlice,
  resolveFramesPerSlice,
  resolveSeriesDirectionalRenderableDeclaredSlice as resolveSeriesDirectionalRenderableDeclaredSliceByState,
  resolveSeriesRenderableDeclaredSliceRuns as resolveSeriesRenderableDeclaredSliceRunsByState,
  resolveSeriesDeclaredSliceIndexFromRenderablePosition as resolveSeriesDeclaredSliceIndexFromRenderablePositionByState,
  resolveSeriesNextRenderableDeclaredSlice as resolveSeriesNextRenderableDeclaredSliceByState,
  resolveSeriesRenderableSliceTarget as resolveSeriesRenderableSliceTargetByState,
  toImageKey,
  uniqueImageIds,
  updateSeriesRenderableCompactOrder
} from "@/js/download/seriesDownload.priority";
import { createSeriesLifecycle } from "@/js/download/seriesDownload.lifecycle";
import { createProcessDownloadQueue } from "@/js/download/seriesDownload.queue";
import { createOnReceiveSeriesDownloadDataHandler } from "@/js/download/seriesDownload.protocol";

export let dm = new DownloadScheduler("alternate", false);

// Track per-series download progress as loaded/total counts.
const downloadCounts = {};
const seriesStateTrackers = {};
const seriesTransportModes = {};
const seriesSchedulingModes = {};
const seriesDeclaredImageOrders = {};
const seriesDeclaredImageIndexMaps = {};
const seriesRenderableImageIds = {};
const seriesRenderableDeclaredByImageKey = {};
const seriesRenderableDeclaredCompact = {};
const seriesFramesPerSlice = {};
const pipelineScopedSeriesStateStores = [
  seriesStateTrackers,
  seriesTransportModes,
  seriesSchedulingModes,
  seriesDeclaredImageOrders,
  seriesDeclaredImageIndexMaps,
  seriesRenderableImageIds,
  seriesRenderableDeclaredByImageKey,
  seriesRenderableDeclaredCompact,
  seriesFramesPerSlice
];
const allSeriesStateStores = [
  ...pipelineScopedSeriesStateStores,
  seriesComponentMapping
];

// Accumulator for in-progress chunked downloads, keyed by db_id (Image PK).
// Each entry: { chunks: Uint8Array[], received: number, totalSize: number|null,
//              seriesId, elementId, imageId, annotations }
const pendingChunks = {};

/**
 * Resolves a human-readable series label from viewer store data.
 * @param {string|number} seriesId
 * @returns {string|null}
 */
const getSeriesLabel = seriesId => {
  try {
    const studies = store.state.viewer.data || [];
    for (const study of studies) {
      const s = (study.series || []).find(s => s.id == seriesId);
      if (s) {
        return (s.series_description || "").trim() || null;
      }
    }
  } catch (_) {
    // ignore — store may not have study data yet
  }
  return null;
};

/**
 * Commits normalized progress percentage for a series.
 * @param {string|number} seriesId
 * @returns {void}
 */
const updateDownloadProgress = seriesId => {
  const entry = downloadCounts[seriesId];
  if (!entry) return;
  const total = Number.isFinite(entry.total) ? Math.max(0, entry.total) : 0;
  const loaded =
    Number.isFinite(entry.loaded) && total > 0
      ? Math.max(0, Math.min(total, entry.loaded))
      : 0;
  const progress = total > 0 ? Math.round((100 * loaded) / total) : 100;
  store.commit("viewer/setDownloadProgress", {
    seriesId,
    value: progress,
    loaded,
    total,
    phase: "download"
  });
};

const URL =
  window.location.protocol == "https:"
    ? `wss:/${window.location.host}/ws/series/download/`
    : `ws:/${window.location.host}/ws/series/download/`;

const wsStatus = new BehaviorSubject();
const DEFAULT_DOWNLOAD_WINDOW = 2;
const MAX_IN_FLIGHT_DOWNLOADS = DEFAULT_DOWNLOAD_WINDOW;

const DEFAULT_MAX_PARSE_QUEUE = 10;
const MAX_PARSE_QUEUE = DEFAULT_MAX_PARSE_QUEUE;
const PARSE_TIMEOUT_MS = 120000;
let inFlightDownloads = 0;
const parseQueue = [];
let isParsing = false;
const pipelineCleanupCallbacks = [];

const runPipelineCleanupCallbacks = () => {
  pipelineCleanupCallbacks.forEach(callback => {
    try {
      callback();
    } catch (error) {
      console.error(error);
    }
  });
  pipelineCleanupCallbacks.length = 0;
};

/**
 * Decrements in-flight download counter by one.
 * @returns {void}
 */
const decrementInFlightDownloads = () => {
  inFlightDownloads = decrementInFlightDownloadsValue(inFlightDownloads);
};

/**
 * Decrements in-flight download counter by a validated amount.
 * @param {number} count
 * @returns {void}
 */
const decrementInFlightDownloadsBy = count => {
  inFlightDownloads = decrementInFlightDownloadsByValue(inFlightDownloads, count);
};

/**
 * Resets pipeline-scoped runtime state after transport reset/disconnect.
 * @returns {void}
 */
const resetPipelineState = () => {
  runPipelineCleanupCallbacks();
  const nextState = resetPipelineRuntimeState({
    parseQueue,
    pendingChunks,
    pipelineScopedSeriesStateStores
  });
  inFlightDownloads = nextState.inFlightDownloads;
  isParsing = nextState.isParsing;
};

export const statusSource = wsStatus;

/**
 * Counts pending chunk accumulators for the provided series.
 * @param {string|number} seriesId
 * @returns {number}
 */
const countPendingChunksForSeries = seriesId =>
  countPendingChunksForSeriesStore(pendingChunks, seriesId);

/**
 * Counts parse queue items for the provided series.
 * @param {string|number} seriesId
 * @returns {number}
 */
const countParseQueueForSeries = seriesId =>
  countParseQueueForSeriesStore(parseQueue, seriesId);

/**
 * Resolves declared slice index for a series/db image pair.
 * @param {string|number} seriesId
 * @param {string|number} dbImageId
 * @returns {number|null}
 */
const getDeclaredSliceIndexForSeries = (seriesId, dbImageId) =>
  getDeclaredSliceIndex(seriesDeclaredImageIndexMaps, seriesId, dbImageId);

/**
 * Removes all runtime state maps tied to the provided series.
 * @param {string|number} seriesId
 * @returns {void}
 */
const clearSeriesRuntimeState = seriesId =>
  clearSeriesRuntimeStateStore({ downloadCounts, allSeriesStateStores }, seriesId);

const { maybeFinalizeSeries, settleSeriesItemWithoutParse, settleBatchItemsWithoutParse } =
  createSeriesLifecycle({
    store,
    downloadPerf,
    dm,
    getSeriesLabel,
    canFinalizeSeriesDownload,
    setSeriesItemInFlight,
    setSeriesItemParseQueued,
    decrementInFlightDownloads,
    countPendingChunksForSeries,
    countParseQueueForSeries,
    seriesStateTrackers,
    clearSeriesRuntimeState
  });

/**
 * Enqueues images for series transport and initializes per-series runtime state.
 * @param {string|number} seriesId
 * @param {string|number} elementId
 * @param {Array<string|number>} images
 * @param {Function} updateProgress
 * @param {Object} [options]
 * @returns {Promise<void>}
 */
export const downloadDicomFiles = async (
  seriesId,
  elementId,
  images,
  updateProgress,
  options = {}
) => {
  seriesComponentMapping[seriesId] = updateProgress;
  const pendingImageIds = uniqueImageIds(images || []);
  const declaredImageOrder = uniqueImageIds(
    Array.isArray(options.declaredImageOrder) && options.declaredImageOrder.length
      ? options.declaredImageOrder
      : pendingImageIds
  );
  const schedulingMode = normalizeDownloadSchedulingMode(options.schedulingMode);
  const framesPerSlice = resolveFramesPerSlice({
    options,
    declaredCount: declaredImageOrder.length
  });
  seriesSchedulingModes[seriesId] = schedulingMode;
  seriesFramesPerSlice[seriesId] = framesPerSlice;
  seriesDeclaredImageOrders[seriesId] = declaredImageOrder;
  seriesDeclaredImageIndexMaps[seriesId] = buildDeclaredImageIndexMap(
    declaredImageOrder
  );
  const pendingImageKeySet = new Set(pendingImageIds.map(toImageKey));
  seriesRenderableImageIds[seriesId] = new Set(
    declaredImageOrder
      .filter(imageId => !pendingImageKeySet.has(toImageKey(imageId)))
      .map(imageId => toImageKey(imageId))
  );
  seriesRenderableDeclaredByImageKey[seriesId] = new Map();
  seriesRenderableDeclaredCompact[seriesId] = declaredImageOrder
    .map((_, index) => index)
    .filter(index => {
      const imageId = declaredImageOrder[index];
      return !pendingImageKeySet.has(toImageKey(imageId));
    });
  // 4D bootstrap should prefer the center frame (not frame 0) while keeping
  // center-out traversal on the slice axis.
  const initialTimeFrameIndex = Number.isFinite(options.initialTimeFrameIndex)
    ? clampSliceIndex(options.initialTimeFrameIndex, framesPerSlice - 1)
    : framesPerSlice > 1
    ? Math.floor((framesPerSlice - 1) / 2)
    : 0;
  const initialActiveAxis =
    options.initialActiveAxis === "slice" ? "slice" : "frame";
  const queueImageIds =
    schedulingMode === DOWNLOAD_SCHEDULING_MODE.PRIORITY &&
    pendingImageIds.length > 1
      ? framesPerSlice > 1
        ? buildPriorityPendingOrderForActiveAxis({
            declaredImageOrder,
            pendingImageIds,
            activeAxis: initialActiveAxis,
            anchorSliceIndex: null,
            anchorFrameIndex: initialTimeFrameIndex,
            framesPerSlice
          })
        : buildPriorityPendingOrder({
            declaredImageOrder,
            pendingImageIds,
            anchorSliceIndex: null,
            framesPerSlice
          })
      : pendingImageIds;

  const totalImages = Number.isInteger(options.totalImages)
    ? options.totalImages
    : pendingImageIds.length;
  const alreadyLoaded = Number.isInteger(options.alreadyLoaded)
    ? options.alreadyLoaded
    : 0;

  // Initialize download counter for this series
  downloadCounts[seriesId] = {
    loaded: alreadyLoaded,
    total: Math.max(totalImages, alreadyLoaded + pendingImageIds.length)
  };
  seriesStateTrackers[seriesId] = createSeriesDownloadTracker(queueImageIds);
  seriesTransportModes[seriesId] = normalizeDownloadTransportMode(
    options.transportMode
  );
  updateDownloadProgress(seriesId);

  downloadPerf.startSeries({
    seriesId,
    elementId,
    totalImages,
    imageIds: queueImageIds,
    label: options.label || getSeriesLabel(seriesId),
    metadata: {
      modality: options.modality || null,
      number_of_slices: options.numberOfSlices ?? null,
      already_loaded: alreadyLoaded,
      transport_mode: seriesTransportModes[seriesId],
      scheduling_mode: schedulingMode,
      frames_per_slice: framesPerSlice,
      initial_time_frame_index:
        framesPerSlice > 1 ? initialTimeFrameIndex : null,
      initial_active_axis: framesPerSlice > 1 ? initialActiveAxis : null,
      download_runtime_config:
        options.downloadRuntimeConfig &&
        typeof options.downloadRuntimeConfig === "object"
          ? options.downloadRuntimeConfig
          : null
    }
  });

  // add series to the download manager
  dm.addSeries(seriesId, seriesId, elementId, queueImageIds);

  // start downloading if the download is not already in progress
  processDownloadQueue();
};

/**
 * Stops scheduling and clears runtime state for a series.
 * @param {string|number} id
 * @returns {void}
 */
export const stopDownloadingDicomFiles = id => {
  dm.removeSeries(id);
  clearSeriesRuntimeState(id);
  store.commit("viewer/setDownloadProgress", { seriesId: id, value: null });
};

/**
 * Resolves best renderable target for a requested declared slice.
 * @param {Object} params
 * @returns {Object|null}
 */
export const resolveSeriesRenderableSliceTarget = ({
  seriesId,
  requestedSliceId
} = {}) =>
  resolveSeriesRenderableSliceTargetByState(
    {
      seriesDeclaredImageOrders,
      seriesRenderableDeclaredCompact,
      seriesDeclaredImageIndexMaps,
      seriesRenderableImageIds
    },
    { seriesId, requestedSliceId }
  );

/**
 * Resolves a renderable declared slice constrained by navigation direction.
 * @param {Object} params
 * @returns {number|null}
 */
export const resolveSeriesDirectionalRenderableDeclaredSlice = ({
  seriesId,
  requestedSliceId,
  direction = 0
} = {}) =>
  resolveSeriesDirectionalRenderableDeclaredSliceByState(
    { seriesRenderableDeclaredCompact },
    { seriesId, requestedSliceId, direction }
  );

/**
 * Resolves contiguous slider runs for currently renderable declared slices.
 * @param {Object} params
 * @returns {Array<{start:number,end:number}>}
 */
export const resolveSeriesRenderableDeclaredSliceRuns = ({
  seriesId,
  framesPerSlice = 1,
  minSliceIndex = null,
  maxSliceIndex = null
} = {}) =>
  resolveSeriesRenderableDeclaredSliceRunsByState(
    {
      seriesRenderableDeclaredCompact,
      seriesDeclaredImageIndexMaps,
      seriesRenderableImageIds
    },
    { seriesId, framesPerSlice, minSliceIndex, maxSliceIndex }
  );

/**
 * Resolves declared slice index from a renderable stack position.
 * @param {Object} params
 * @returns {number|null}
 */
export const resolveSeriesDeclaredSliceIndexFromRenderablePosition = ({
  seriesId,
  renderablePosition
} = {}) =>
  resolveSeriesDeclaredSliceIndexFromRenderablePositionByState(
    {
      seriesRenderableDeclaredCompact,
      seriesDeclaredImageIndexMaps,
      seriesRenderableImageIds
    },
    { seriesId, renderablePosition }
  );

/**
 * Resolves next declared slice index that is currently renderable.
 * @param {Object} params
 * @returns {number|null}
 */
export const resolveSeriesNextRenderableDeclaredSlice = ({
  seriesId,
  currentDeclaredSliceIndex,
  direction = 1
} = {}) =>
  resolveSeriesNextRenderableDeclaredSliceByState(
    { seriesRenderableDeclaredCompact },
    { seriesId, currentDeclaredSliceIndex, direction }
  );

const resolveSeriesReprioritizeContext = ({
  seriesId,
  requireTemporalFrames = false
} = {}) => {
  if (seriesId === null || seriesId === undefined) {
    return null;
  }
  if (seriesSchedulingModes[seriesId] !== DOWNLOAD_SCHEDULING_MODE.PRIORITY) {
    return null;
  }

  const declaredImageOrder = seriesDeclaredImageOrders[seriesId] || [];
  const framesPerSlice = normalizeFramesPerSlice(seriesFramesPerSlice[seriesId]);
  if (
    declaredImageOrder.length <= 1 ||
    (requireTemporalFrames && framesPerSlice <= 1)
  ) {
    return null;
  }

  const pendingImageIds = dm.getSeriesPendingImageIds(seriesId);
  if (!pendingImageIds.length) {
    return null;
  }

  return {
    declaredImageOrder,
    framesPerSlice,
    pendingImageIds
  };
};

const applySeriesReprioritize = ({ seriesId, nextOrder, perfPayload }) => {
  if (!Array.isArray(nextOrder) || !nextOrder.length) {
    return false;
  }

  const changed = dm.reprioritizeSeries(seriesId, nextOrder);
  if (!changed) {
    return false;
  }

  downloadPerf.markQueueReprioritize(perfPayload);
  processDownloadQueue();
  return true;
};

/**
 * Reorders pending queue around a requested slice when priority scheduling is active.
 * @param {Object} params
 * @returns {boolean}
 */
export const reprioritizeSeriesDownloadAroundSlice = ({
  seriesId,
  requestedSliceId,
  elementId = null,
  loadedImageCount = null,
  declaredMaxSliceId = null,
  reason = "viewer_navigation"
} = {}) => {
  const context = resolveSeriesReprioritizeContext({ seriesId });
  if (!context) {
    return false;
  }
  const { declaredImageOrder, framesPerSlice, pendingImageIds } = context;

  const anchorSliceIndex = clampSliceIndex(
    requestedSliceId,
    declaredImageOrder.length - 1
  );
  const nextOrder = buildPriorityPendingOrder({
    declaredImageOrder,
    pendingImageIds,
    anchorSliceIndex,
    framesPerSlice
  });
  return applySeriesReprioritize({
    seriesId,
    nextOrder,
    perfPayload: {
      seriesId,
      requestedSliceId: anchorSliceIndex,
      reason,
      pendingCount: pendingImageIds.length,
      loadedImageCount: Number.isFinite(loadedImageCount)
        ? loadedImageCount
        : null,
      declaredMaxSliceId: Number.isFinite(declaredMaxSliceId)
        ? declaredMaxSliceId
        : null,
      elementId
    }
  });
};

/**
 * Reorders pending queue by timeframe and optional anchor slice.
 * @param {Object} params
 * @returns {boolean}
 */
export const reprioritizeSeriesDownloadForTimeFrame = ({
  seriesId,
  timeFrameIndex,
  requestedSliceId = null,
  elementId = null,
  reason = "viewer_scroll_mode_slice"
} = {}) => {
  const context = resolveSeriesReprioritizeContext({
    seriesId,
    requireTemporalFrames: true
  });
  if (!context) {
    return false;
  }
  const { declaredImageOrder, framesPerSlice, pendingImageIds } = context;

  const anchorSliceIndex = Number.isFinite(requestedSliceId)
    ? clampSliceIndex(requestedSliceId, declaredImageOrder.length - 1)
    : null;
  const nextOrder = buildPriorityPendingOrderForTimeFrame({
    declaredImageOrder,
    pendingImageIds,
    timeFrameIndex,
    anchorSliceIndex,
    framesPerSlice
  });
  return applySeriesReprioritize({
    seriesId,
    nextOrder,
    perfPayload: {
      seriesId,
      requestedSliceId: Number.isFinite(anchorSliceIndex)
        ? anchorSliceIndex
        : null,
      reason,
      pendingCount: pendingImageIds.length,
      elementId,
      timeFrameIndex: Number.isFinite(timeFrameIndex)
        ? Math.floor(timeFrameIndex)
        : null
    }
  });
};

/**
 * Reorders 4D pending queue with double center-out based on active scroll axis.
 * @param {Object} params
 * @returns {boolean}
 */
export const reprioritizeSeriesDownloadForActiveAxis = ({
  seriesId,
  activeAxis = "frame",
  anchorSliceIndex = null,
  anchorFrameIndex = null,
  elementId = null,
  reason = "viewer_4d_active_axis"
} = {}) => {
  const context = resolveSeriesReprioritizeContext({
    seriesId,
    requireTemporalFrames: true
  });
  if (!context) {
    return false;
  }
  const { declaredImageOrder, framesPerSlice, pendingImageIds } = context;

  const totalSlices = Math.ceil(declaredImageOrder.length / framesPerSlice);
  const normalizedSliceAnchor = Number.isFinite(anchorSliceIndex)
    ? clampSliceIndex(anchorSliceIndex, Math.max(0, totalSlices - 1))
    : null;
  const normalizedFrameAnchor = Number.isFinite(anchorFrameIndex)
    ? clampSliceIndex(anchorFrameIndex, framesPerSlice - 1)
    : null;
  const nextOrder = buildPriorityPendingOrderForActiveAxis({
    declaredImageOrder,
    pendingImageIds,
    activeAxis,
    anchorSliceIndex: normalizedSliceAnchor,
    anchorFrameIndex: normalizedFrameAnchor,
    framesPerSlice
  });
  return applySeriesReprioritize({
    seriesId,
    nextOrder,
    perfPayload: {
      seriesId,
      requestedSliceId: Number.isFinite(normalizedSliceAnchor)
        ? normalizedSliceAnchor
        : null,
      reason: `${reason}:${activeAxis}`,
      pendingCount: pendingImageIds.length,
      elementId
    }
  });
};

/**
 * Pushes parse task in queue and marks tracker parseQueued=true.
 * @param {Object} task
 * @returns {void}
 */
const enqueueParseTask = task => {
  parseQueue.push(task);
  setSeriesItemParseQueued(
    seriesStateTrackers[task.seriesId],
    task.dbImageId,
    true
  );
};

// Record transport completion and move item state to RECEIVED before parsing.
/**
 * Marks one series item as received at transport level.
 * @param {Object} payload
 * @returns {void}
 */
const markSeriesItemReceived = ({
  seriesId,
  dbImageId,
  requestId = null,
  payloadSize,
  chunkIndex = 0
}) => {
  downloadPerf.markFirstChunk({
    seriesId,
    dbImageId,
    requestId,
    totalSize: payloadSize
  });
  downloadPerf.markChunk({
    seriesId,
    dbImageId,
    requestId,
    bytes: payloadSize,
    chunkIndex,
    inFlight: inFlightDownloads,
    parseQueueLength: parseQueue.length,
    pendingChunkCount: Object.keys(pendingChunks).length
  });
  transitionSeriesItemState(
    seriesStateTrackers[seriesId],
    dbImageId,
    ITEM_STATES.RECEIVED
  );
  downloadPerf.markLastChunk({
    seriesId,
    dbImageId,
    requestId,
    parseQueueLength: parseQueue.length
  });
  downloadPerf.markItemState({
    seriesId,
    dbImageId,
    requestId,
    state: ITEM_STATES.RECEIVED
  });
};

/**
 * Enqueues a parse task from raw DICOM bytes payload.
 * @param {Object} payload
 * @returns {void}
 */
const enqueueParseBytesTask = ({
  seriesId,
  elementId,
  dbImageId,
  requestId = null,
  fileName,
  byteArray
}) => {
  enqueueParseTask({
    seriesId,
    elementId,
    dbImageId,
    requestId,
    sliceIndex: getDeclaredSliceIndexForSeries(seriesId, dbImageId),
    byteArray,
    fileName
  });
};

const withTimeout = (promise, timeoutMs, message) => {
  let timeoutId = null;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  });
};

const processDownloadQueueInternal = createProcessDownloadQueue({
  getSocket: () => ws,
  webSocketOpenState: WebSocket.OPEN,
  parseQueue,
  maxParseQueue: MAX_PARSE_QUEUE,
  getInFlightDownloads: () => inFlightDownloads,
  setInFlightDownloads: value => {
    inFlightDownloads = value;
  },
  maxInFlightDownloads: MAX_IN_FLIGHT_DOWNLOADS,
  dm,
  seriesTransportModes,
  DOWNLOAD_TRANSPORT_MODE,
  getDownloadBatchRequestMaxItems,
  sendMessage: message => ws.send(message),
  setSeriesItemInFlight,
  seriesStateTrackers,
  downloadPerf,
  pendingChunks
});

// Interface with the download scheduler and transport layer.
/**
 * Tries to dispatch new websocket requests while runtime windows allow it.
 * @returns {void}
 */
export function processDownloadQueue() {
  processDownloadQueueInternal();
}

/**
 * Drains parse queue serially and updates tracker/progress/perf signals.
 * @returns {Promise<void>}
 */
async function drainParseQueue() {
  if (isParsing) return;
  isParsing = true;

  while (parseQueue.length > 0) {
    const task = parseQueue.shift();
    setSeriesItemParseQueued(
      seriesStateTrackers[task.seriesId],
      task.dbImageId,
      false
    );

    try {
      const parserUsed =
        task.byteArray && supportsParseBytes(Vue.prototype.$dicom)
          ? "parseBytes"
          : "parseFile";
      const progressCounts = downloadCounts[task.seriesId] || {
        loaded: 0,
        total: 0
      };
      const parseLoaded =
        progressCounts.total > 0
          ? Math.min(progressCounts.total, (progressCounts.loaded || 0) + 1)
          : 0;
      store.commit("viewer/setDownloadProgress", {
        seriesId: task.seriesId,
        value:
          progressCounts.total > 0
            ? Math.round((100 * parseLoaded) / progressCounts.total)
            : 0,
        loaded: progressCounts.loaded || 0,
        total: progressCounts.total || 0,
        phase: "parse"
      });
      downloadPerf.markParseStart({
        seriesId: task.seriesId,
        dbImageId: task.dbImageId,
        requestId: task.requestId,
        parserUsed,
        parseQueueLength: parseQueue.length
      });
      const fallbackFile = task.file
        ? task.file
        : task.byteArray
        ? new File([task.byteArray], task.fileName || "dicom-bytes.dcm", {
            type: "application/dicom"
          })
        : null;
      let parsedResult;
      if (parserUsed === "parseBytes") {
        parsedResult = await withTimeout(
          Vue.prototype.$dicom.parseBytes(
            task.seriesId,
            task.byteArray,
            task.fileName,
            task.elementId
          ),
          PARSE_TIMEOUT_MS,
          `DICOM parse timeout for image ${task.dbImageId}`
        );
      } else {
        if (!fallbackFile) {
          throw new Error("Missing DICOM parse input");
        }
        parsedResult = await withTimeout(
          Vue.prototype.$dicom.parseFile(
            task.seriesId,
            fallbackFile,
            task.elementId
          ),
          PARSE_TIMEOUT_MS,
          `DICOM parse timeout for image ${task.dbImageId}`
        );
      }
      const { imageIds, imageId } = parsedResult;
      downloadPerf.markParseEnd({
        seriesId: task.seriesId,
        dbImageId: task.dbImageId,
        requestId: task.requestId
      });
      transitionSeriesItemState(
        seriesStateTrackers[task.seriesId],
        task.dbImageId,
        ITEM_STATES.PARSED
      );
      downloadPerf.markItemState({
        seriesId: task.seriesId,
        dbImageId: task.dbImageId,
        requestId: task.requestId,
        state: ITEM_STATES.PARSED
      });

      if (typeof seriesComponentMapping[task.seriesId] === "function") {
        const declaredSliceIndex = getDeclaredSliceIndexForSeries(
          task.seriesId,
          task.dbImageId
        );
        updateSeriesRenderableCompactOrder(
          { seriesRenderableDeclaredByImageKey, seriesRenderableDeclaredCompact },
          {
            seriesId: task.seriesId,
            imageIds,
            managerImageId: imageId,
            declaredSliceIndex
          }
        );
        seriesComponentMapping[task.seriesId](
          task.seriesId,
          task.elementId,
          imageIds,
          declaredSliceIndex,
          imageId
        );
      }
      downloadPerf.markStackUpdate({
        seriesId: task.seriesId,
        dbImageId: task.dbImageId,
        requestId: task.requestId,
        imageIdsLength: Array.isArray(imageIds) ? imageIds.length : null,
        currentImageId: imageId
      });
      transitionSeriesItemState(
        seriesStateTrackers[task.seriesId],
        task.dbImageId,
        ITEM_STATES.RENDERABLE
      );
      addSeriesRenderableImage(
        seriesRenderableImageIds,
        task.seriesId,
        task.dbImageId
      );
      downloadPerf.markItemState({
        seriesId: task.seriesId,
        dbImageId: task.dbImageId,
        requestId: task.requestId,
        state: ITEM_STATES.RENDERABLE
      });

      if (downloadCounts[task.seriesId]) {
        downloadCounts[task.seriesId].loaded++;
        updateDownloadProgress(task.seriesId);
      }
    } catch (error) {
      console.error(error);
      downloadPerf.recordError({
        seriesId: task.seriesId,
        dbImageId: task.dbImageId,
        requestId: task.requestId,
        stage: "parse_or_stack_update",
        message: error?.message || error
      });
      // Failed parse/render is terminal for this image; otherwise the series
      // remains stuck forever waiting for an item that can never be renderable.
      transitionSeriesItemState(
        seriesStateTrackers[task.seriesId],
        task.dbImageId,
        ITEM_STATES.FAILED
      );
      downloadPerf.markItemState({
        seriesId: task.seriesId,
        dbImageId: task.dbImageId,
        requestId: task.requestId,
        state: ITEM_STATES.FAILED
      });
      store.dispatch("raiseSnackbar", {
        text: error || "Failed to download series",
        color: "error",
        timeout: -1
      });
    } finally {
      decrementInFlightDownloads();
      setSeriesItemInFlight(
        seriesStateTrackers[task.seriesId],
        task.dbImageId,
        false
      );
      dm.clearInFlight(task.seriesId, task.dbImageId);
      maybeFinalizeSeries(task.seriesId, "parse_task_finally");
      processDownloadQueue();
      await yieldToMainThread();
    }
  }

  isParsing = false;
}

/**
 * Opens download websocket and binds transport/protocol handlers.
 * @param {string|number} userID
 * @param {Function} onWsClose
 * @param {Function} onWsError
 * @param {Object} router
 * @returns {void}
 */
export function openSeriesWebsocket(userID, onWsClose, onWsError, router) {
  if (ws) return;
  const onClose = event => {
    const readyState = ws ? ws.readyState : WebSocket.CLOSED;
    resetPipelineState();
    wsStatus.next(readyState);
    onWsClose(event);
  };

  const onError = event => {
    const readyState = ws ? ws.readyState : WebSocket.CLOSED;
    resetPipelineState();
    if (readyState == WebSocket.OPEN) {
      onWsError(event);
    }
  };

  ws = new WebSocket(URL + userID + `/`);
  wsStatus.next(ws.readyState);

  ws.onopen = () => {
    wsStatus.next(ws.readyState);
    processDownloadQueue();
  };

  onCloseListener = event => onClose(event);
  onErrorListener = event => onError(event);

  ws.onclose = onCloseListener;
  ws.onerror = onErrorListener;

  const emitFatalSocketPayloadError = params =>
    handleFatalSocketPayloadError({
      downloadPerf,
      store,
      disconnect,
      ...params
    });

  runPipelineCleanupCallbacks();

  // Bind runtime state and side effects used by the WS protocol handler.
  const onReceiveData = createOnReceiveSeriesDownloadDataHandler({
    router,
    dm,
    decrementInFlightDownloads,
    decrementInFlightDownloadsBy,
    processDownloadQueue,
    emitFatalSocketPayloadError,
    DOWNLOAD_TRANSPORT_MODE,
    pendingChunks,
    settleSeriesItemWithoutParse,
    settleBatchItemsWithoutParse,
    setSeriesItemInFlight,
    setSeriesItemParseQueued,
    seriesStateTrackers,
    maybeFinalizeSeries,
    toUint8Array,
    transitionSeriesItemState,
    ITEM_STATES,
    downloadPerf,
    store,
    downloadCounts,
    getInFlightDownloads: () => inFlightDownloads,
    parseQueue,
    enqueueParseTask,
    drainParseQueue,
    getDeclaredSliceIndexForSeries,
    markSeriesItemReceived,
    enqueueParseBytesTask,
    registerCleanup: callback => {
      pipelineCleanupCallbacks.push(callback);
    }
  });

  setOnMessage(onReceiveData);
}

/**
 * Sets websocket onmessage callback.
 * @param {Function} onmessage
 * @returns {void}
 */
export function setOnMessage(onmessage) {
  ws.onmessage = onmessage;
}

/**
 * Sets websocket onerror callback.
 * @param {Function} onerror
 * @returns {void}
 */
export function setOnError(onerror) {
  ws.onerror = onerror;
}

/**
 * Returns true when transport can send websocket messages.
 * @returns {boolean}
 */
export const canSendMessage = () => {
  return ws !== null && typeof ws.send === "function";
};

/**
 * Sends raw message through websocket.
 * @param {string} message
 * @returns {void}
 */
export const send = message => ws.send(message);

/**
 * Closes websocket transport and resets runtime pipeline state.
 * @returns {void}
 */
export const disconnect = () => {
  if (ws && typeof ws.close === "function") {
    ws.close();
    ws = null;
    resetPipelineState();
  }
};
